import {
  Button,
  ChevronDown,
  ChevronUp,
  Cookie,
} from "@ews-admin/global-design-system";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { updateAnalyticsConsent } from "../../../analytics/ga";

export const CookieConsent: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true and cannot be changed
    analytics: false,
    marketing: false,
    preferences: false,
  });
  const { t } = useTranslation();

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem("cookieConsent");
    if (!consent) {
      setIsVisible(true);
    } else {
      setPreferences(JSON.parse(consent));
    }
  }, []);

  const handleAcceptAll = () => {
    const allConsent = {
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true,
    };
    localStorage.setItem("cookieConsent", JSON.stringify(allConsent));
    updateAnalyticsConsent(true);
    setPreferences(allConsent);
    setIsVisible(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem("cookieConsent", JSON.stringify(preferences));
    updateAnalyticsConsent(preferences.analytics);
    setIsVisible(false);
  };

  const handleRejectAll = () => {
    const minimalConsent = {
      necessary: true,
      analytics: false,
      marketing: false,
      preferences: false,
    };
    localStorage.setItem("cookieConsent", JSON.stringify(minimalConsent));
    updateAnalyticsConsent(false);
    setPreferences(minimalConsent);
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 border-t border-gray-200 bg-white shadow-lg">
      <div className="mx-auto max-w-7xl p-4 sm:px-6 lg:px-8">
        <div className="flex flex-col space-y-4">
          {/* Main Banner */}
          <div className="flex items-start justify-between">
            <div className="flex items-start">
              <Cookie className="mr-3 mt-1 h-6 w-6 text-ews-primary" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {t("cookies.cookieSettings")}
                </h3>
                <p className="mt-1 text-sm text-gray-600">
                  {t("cookies.cookiePolicy")}
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="hover:text-ews-primary/80 ml-4 flex items-center text-sm text-ews-primary"
            >
              {showDetails ? (
                <>
                  <ChevronUp className="mr-1 h-4 w-4" />
                  {t("common.hideDetails")}
                </>
              ) : (
                <>
                  <ChevronDown className="mr-1 h-4 w-4" />
                  {t("common.showDetails")}
                </>
              )}
            </button>
          </div>

          {/* Detailed Settings */}
          {showDetails && (
            <div className="grid grid-cols-1 gap-4 border-t border-gray-200 pt-4 md:grid-cols-2 lg:grid-cols-4">
              {/* Necessary Cookies */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="font-medium text-gray-900">
                    {t("cookies.necessary")}
                  </label>
                  <input
                    type="checkbox"
                    checked={preferences.necessary}
                    disabled
                    className="rounded border-gray-300 text-ews-primary focus:ring-ews-primary"
                  />
                </div>
                <p className="text-sm text-gray-600">
                  {t("cookies.essential")}
                </p>
              </div>

              {/* Analytics Cookies */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="font-medium text-gray-900">
                    {t("cookies.analytics")}
                  </label>
                  <input
                    type="checkbox"
                    checked={preferences.analytics}
                    onChange={e =>
                      setPreferences({
                        ...preferences,
                        analytics: e.target.checked,
                      })
                    }
                    className="rounded border-gray-300 text-ews-primary focus:ring-ews-primary"
                  />
                </div>
                <p className="text-sm text-gray-600">
                  {t("cookies.helpUsUnderstand")}
                </p>
              </div>

              {/* Marketing Cookies */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="font-medium text-gray-900">
                    {t("cookies.marketing")}
                  </label>
                  <input
                    type="checkbox"
                    checked={preferences.marketing}
                    onChange={e =>
                      setPreferences({
                        ...preferences,
                        marketing: e.target.checked,
                      })
                    }
                    className="rounded border-gray-300 text-ews-primary focus:ring-ews-primary"
                  />
                </div>
                <p className="text-sm text-gray-600">
                  {t("cookies.personalizedAdvertisements")}
                </p>
              </div>

              {/* Preferences Cookies */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="font-medium text-gray-900">
                    {t("cookies.preferences")}
                  </label>
                  <input
                    type="checkbox"
                    checked={preferences.preferences}
                    onChange={e =>
                      setPreferences({
                        ...preferences,
                        preferences: e.target.checked,
                      })
                    }
                    className="rounded border-gray-300 text-ews-primary focus:ring-ews-primary"
                  />
                </div>
                <p className="text-sm text-gray-600">
                  {t("cookies.rememberSettings")}
                </p>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col justify-end space-y-2 border-t border-gray-200 pt-4 sm:flex-row sm:space-x-2 sm:space-y-0">
            <Button variant="ghost" onClick={handleRejectAll}>
              {t("cookies.rejectAll")}
            </Button>
            <Button variant="ghost" onClick={handleSavePreferences}>
              {t("cookies.savePreferences")}
            </Button>
            <Button onClick={handleAcceptAll}>{t("cookies.acceptAll")}</Button>
          </div>
        </div>
      </div>
    </div>
  );
};
